import {
    errorFetchingRequestState,
    getInitialRequestState,
    RequestState,
    startFetchingRequestState,
    successFetchingRequestState
} from "../../models/common/RequestState";
import {Nullable} from "../../models/common/Nullable";
import {
    FETCH_PROJECT_PRIVILEGES, FETCH_PROJECT_PRIVILEGES_ERROR,
    FETCH_PROJECT_PRIVILEGES_SUCCESS, ProjectPrivilegesActionTypes
} from "../../actions/projects/ProjectPrivilegesActions";
import {ProjectUserPrivileges} from "../../models/projects/ProjectUserPrivileges";

/**
 * Represents the state of user privileges for projects within the system.
 *
 * This interface is designed to manage and track the detailed user privileges associated with specific projects, along with the current state of the corresponding data request.
 * It is utilized to ensure the appropriate representation of user access rights in various project-related workflows.
 */
export interface ProjectPrivilegesState {
    detailedProjects: {
        [ key: number ]: Nullable< ProjectUserPrivileges >
    }
    request: RequestState
}

/**
 * Represents the initial state for managing project privileges within the application.
 * This state is primarily intended to track detailed project-related data and any active requests.
 *
 * The `initialState` provides a default baseline for runtime behavior and ensures consistency
 * in how project privileges are handled across the system.
 *
 * Detailed Explanation:
 * - `detailedProjects`: An object that serves as a lookup for individual project details, keyed by project identifier. Initially set to an empty object.
 * - `request`: Manages the state of ongoing requests related to project privilege actions. Initialized using `getInitialRequestState` to capture defaults for request tracking (e.g., loading, success, or error states).
 *
 * This state must be updated or extended cautiously to preserve system reliability, especially around asynchronous operations or bulk project data handling.
 */
const initialState: ProjectPrivilegesState = {
    detailedProjects: {},
    request: getInitialRequestState()
};

/**
 * Manages and updates the state related to project-specific user privileges.
 *
 * This reducer is responsible for handling the state transitions for project privileges, enabling the application
 * to fetch, store, and manage privilege details for individual projects. It supports actions for fetching privileges,
 * handling success responses, and logging errors. This ensures smooth and consistent state management in
 * collaboration-heavy workflows.
 *
 * The state structure includes privilege details for each project along with a request status for tracking
 * API calls. The state changes are triggered by specific action types, each dictating how the state should be updated.
 * Detailed information for each project is maintained under the `detailedProjects` property.
 *
 * @param state - The current state of the project privileges. Defaults to `initialState` if not provided.
 * @param action - The dispatched action that describes the operation to be performed on the state.
 *                 Should conform to `ProjectPrivilegesActionTypes`.
 *
 * @return The updated state after applying the specified action.
 *
 * @throws Will not throw any errors internally, but misusing the action payloads or unsupported action*/
/**
 * Manages and updates the state related to project-specific user privileges.
 *
 * This reducer is responsible for handling the state transitions for project privileges, enabling the application
 * to fetch, store, and manage privilege details for individual projects. It supports actions for fetching privileges,
 * handling success responses, and logging errors. This ensures smooth and consistent state management in
 * collaboration-heavy workflows.
 *
 * The state structure includes privilege details for each project along with a request status for tracking
 * API calls. The state changes are triggered by specific action types, each dictating how the state should be updated.
 * Detailed information for each project is maintained under the `detailedProjects` property.
 *
 * @param state - The current state of the project privileges. Defaults to `initialState` if not provided.
 * @param action - The dispatched action that describes the operation to be performed on the state.
 *                 Should conform to `ProjectPrivilegesActionTypes`.
 *
 * @return The updated state after applying the specified action.
 *
 * @throws Will not throw any errors internally, but misusing the action payloads or unsupported action types
 *         could result in incorrect state changes.
 */
export function ProjectPrivilegesReducer( state = initialState, action: ProjectPrivilegesActionTypes ) {

    switch( action.type ) {
        case FETCH_PROJECT_PRIVILEGES:
            return {
                ...state,
                detailedProjects: {
                    ...state.detailedProjects,
                    [ action.projectId.rawValue ]: {
                        ...state.detailedProjects[ action.projectId.rawValue ]
                    }
                },
                request: startFetchingRequestState()
            };
        case FETCH_PROJECT_PRIVILEGES_SUCCESS:
            return {
                ...state,
                detailedProjects: {
                    ...state.detailedProjects,
                    [ action.projectId.rawValue ]: action.payload

                },
                request: successFetchingRequestState()
            };
        case FETCH_PROJECT_PRIVILEGES_ERROR:
            return {
                ...state,
                request: errorFetchingRequestState( action.error )
            };
        default:
            return state;
    }

}